import api from './api'

const leadService = {
  // PUBLIC - ứng viên nộp form
  async submit(data) {
    const response = await api.post('/leads', data)
    return response.data
  },

  // PUBLIC - upload file CV (pdf/doc/docx <= 5MB) -> { url, filename }
  async uploadCv(file) {
    const fd = new FormData()
    fd.append('file', file)
    const response = await api.post('/leads/upload-cv', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // AUTH
  async list(params = {}) {
    const response = await api.get('/leads', { params })
    return response.data
  },

  async stats() {
    const response = await api.get('/leads/stats')
    return response.data
  },

  async overview() {
    const response = await api.get('/leads/overview')
    return response.data
  },

  async notifications() {
    const response = await api.get('/leads/notifications')
    return response.data
  },
  // Đánh dấu đã đọc: truyền id để đọc 1, bỏ trống = đọc tất cả
  async markNotifRead(id) {
    const response = await api.post('/leads/notifications/read', id ? { id } : {})
    return response.data
  },
  // Dọn dẹp (xóa hết thông báo của mình)
  async clearNotifs() {
    const response = await api.delete('/leads/notifications')
    return response.data
  },
  // Cài đặt nhận thông báo (vd nhận email) — { email_notify: bool }
  async updateNotifSettings(payload) {
    const response = await api.put('/leads/notif-settings', payload)
    return response.data
  },

  async report(params = {}) {
    const response = await api.get('/leads/report', { params })
    return response.data
  },

  async filterOptions() {
    const response = await api.get('/leads/filter-options')
    return response.data
  },
  async pin(id) {
    const response = await api.post(`/leads/${id}/pin`)
    return response.data
  },
  async unpin(id) {
    const response = await api.delete(`/leads/${id}/pin`)
    return response.data
  },

  async get(id) {
    const response = await api.get(`/leads/${id}`)
    return response.data
  },

  async update(id, data) {
    const response = await api.put(`/leads/${id}`, data)
    return response.data
  },

  async remove(id, reason) {
    const response = await api.delete(`/leads/${id}`, { data: { reason: reason || null } })
    return response.data
  },

  // Lịch sử xóa ứng viên (admin/quản lý/trưởng nhóm)
  async deletionLogs(params = {}) {
    const response = await api.get('/leads/deletion-logs', { params })
    return response.data
  },

  // Tải CV qua endpoint có xác thực (Bearer tự đính kèm) -> Blob
  async downloadCvBlob(id) {
    const response = await api.get(`/leads/${id}/cv`, { responseType: 'blob' })
    return response.data
  },

  // Phân luồng nhiều lead cho 1 nhân viên
  async assign(leadIds, assignedTo) {
    const response = await api.put('/leads/assign', { lead_ids: leadIds, assigned_to: assignedTo })
    return response.data
  },

  // Nhật ký + ghi chú nhanh
  async activities(id) {
    const response = await api.get(`/leads/${id}/activities`)
    return response.data
  },
  async addNote(id, content) {
    const response = await api.post(`/leads/${id}/notes`, { content })
    return response.data
  },
  // Thêm 1 mốc tư vấn (timeline): { content, outcome, next_action_at, status? }
  async addContactLog(id, payload) {
    const response = await api.post(`/leads/${id}/contact-log`, payload)
    return response.data
  },
}

// Kết quả cuộc gọi (outcome) cho form tư vấn — nhãn emoji
export const CALL_OUTCOMES = [
  { value: 'khong_bat_may', label: '📵 Không bắt máy' },
  { value: 'thue_bao', label: '📴 Thuê bao / tắt máy' },
  { value: 'da_lien_he', label: '✅ Đã liên hệ được' },
  { value: 'hen_goi_lai', label: '⏰ Hẹn gọi lại' },
  { value: 'hen_pv', label: '📅 Hẹn phỏng vấn' },
  { value: 'tu_choi', label: '🚫 Từ chối / không nhu cầu' },
  { value: 'khac', label: '📝 Khác' },
]
export const CALL_OUTCOME_LABELS = CALL_OUTCOMES.reduce((a, o) => { a[o.value] = o.label; return a }, {})

// Màu chip cho "Người liên hệ" — hash ổn định theo tên/id
const CHIP_COLORS = [
  'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700', 'bg-violet-100 text-violet-700', 'bg-cyan-100 text-cyan-700',
  'bg-orange-100 text-orange-700', 'bg-teal-100 text-teal-700', 'bg-fuchsia-100 text-fuchsia-700',
]
export const chipColor = (key) => {
  const s = String(key ?? '')
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return CHIP_COLORS[h % CHIP_COLORS.length]
}

export const ACTIVITY_LABELS = {
  note: 'Ghi chú', status: 'Đổi trạng thái', assign: 'Phân công', region: 'Khu vực', call: 'Tư vấn',
}

// ╔══════════════════════════════════════════════════════╗
// ║  18 TRẠNG THÁI CHUẨN CRM v9.0 — 4 nhóm funnel          ║
// ╚══════════════════════════════════════════════════════╝
// Mỗi nhóm: { key, label, badge (class màu chung), statuses: [{ value, label }] }
export const LEAD_STATUS_GROUPS = [
  {
    key: 'untouched',
    label: 'Chưa tiếp cận',
    badge: 'badge-neutral',
    statuses: [
      { value: 'new', label: '🆕 Mới (chưa gọi)' },
      { value: 'no_answer', label: '📞 Không nghe máy' },
      { value: 'unreachable', label: '📵 Thuê bao' },
      { value: 'busy', label: '☎️ Máy bận' },
      { value: 'wrong_number', label: '❌ Sai SĐT' },
    ],
  },
  {
    key: 'processing',
    label: 'Đang xử lý',
    badge: 'badge-warning',
    statuses: [
      { value: 'zalo_contacted', label: '💬 Đã liên hệ Zalo' },
      { value: 'discussing', label: '🤝 Đang trao đổi' },
      { value: 'callback', label: '⏰ Hẹn gọi lại' },
    ],
  },
  {
    key: 'positive',
    label: 'Tích cực',
    badge: 'badge-success',
    statuses: [
      { value: 'interview_scheduled', label: '✅ Đã hẹn PV' },
      { value: 'interview_attended', label: '🎯 Đã đến PV' },
      { value: 'passed_waiting', label: '✔️ Đạt chờ nhận việc' },
      { value: 'hired', label: '💼 Đã nhận việc' },
      { value: 'hired_subsidiary', label: '🏢 Nhận việc công ty con' },
    ],
  },
  {
    key: 'rejected',
    label: 'Từ chối',
    badge: 'badge-danger',
    statuses: [
      { value: 'no_need', label: '🚫 Không có nhu cầu' },
      { value: 'not_suitable', label: '👴 Không phù hợp (tuổi/KN)' },
      { value: 'other_area', label: '📍 Khác tỉnh/khu vực' },
      { value: 'interview_failed', label: '❌ PV không đạt' },
      { value: 'declined', label: '🚪 Không nhận việc' },
    ],
  },
]

// Tra cứu nhanh: slug -> nhãn emoji
export const LEAD_STATUS_LABELS = LEAD_STATUS_GROUPS.reduce((acc, g) => {
  g.statuses.forEach((s) => { acc[s.value] = s.label })
  return acc
}, {})

// Tra cứu nhanh: slug -> class badge (theo màu nhóm)
export const LEAD_STATUS_BADGE = LEAD_STATUS_GROUPS.reduce((acc, g) => {
  g.statuses.forEach((s) => { acc[s.value] = g.badge })
  return acc
}, {})

// Tra cứu nhanh: slug -> key nhóm
export const LEAD_STATUS_TO_GROUP = LEAD_STATUS_GROUPS.reduce((acc, g) => {
  g.statuses.forEach((s) => { acc[s.value] = g.key })
  return acc
}, {})

// Meta nhóm funnel (dùng cho dashboard)
export const GROUP_META = LEAD_STATUS_GROUPS.map((g) => ({
  key: g.key, label: g.label, badge: g.badge,
}))

export default leadService
